/**
 * OAuth Popup Utility
 * 
 * Handles opening OAuth flows in popup windows and communicating results
 * back to the parent window. Works seamlessly within iframes.
 */

export interface OAuthPopupResult {
  success: boolean
  error?: string
  data?: unknown
}

interface OAuthPopupOptions {
  url: string
  width?: number
  height?: number
  title?: string
}

/**
 * Opens OAuth URL in a centered popup window
 * Returns a promise that resolves when OAuth completes or popup closes
 */
export function openOAuthPopup({
  url,
  width = 600,
  height = 700,
  title = 'OAuth Authorization'
}: OAuthPopupOptions): Promise<OAuthPopupResult> {
  return new Promise((resolve) => {
    // Calculate center position
    const left = window.screen.width / 2 - width / 2
    const top = window.screen.height / 2 - height / 2

    // Popup features
    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      'toolbar=no',
      'location=no',
      'directories=no',
      'status=no',
      'menubar=no',
      'scrollbars=yes',
      'resizable=yes',
      'copyhistory=no'
    ].join(',')

    // Open popup
    const popup = window.open(url, title, features)

    if (!popup) {
      resolve({
        success: false,
        error: 'Popup was blocked. Please allow popups for this site.'
      })
      return
    }

    // Focus the popup
    popup.focus()

    let resolved = false

    // Listen for messages from the popup
    const handleMessage = (event: MessageEvent) => {
      // Validate origin (same origin as current window)
      if (event.origin !== window.location.origin) {
        return
      }

      // Check if message is from our OAuth callback
      if (event.data && event.data.type === 'oauth-callback') {
        if (!resolved) {
          resolved = true
          cleanup()
          resolve({
            success: event.data.success,
            error: event.data.error,
            data: event.data.data
          })
        }
      }
    }

    // Check if popup was closed manually
    const checkPopupClosed = setInterval(() => {
      if (popup.closed && !resolved) {
        resolved = true
        cleanup()
        resolve({
          success: false,
          error: 'OAuth popup was closed'
        })
      }
    }, 500)

    // Cleanup function
    const cleanup = () => {
      clearInterval(checkPopupClosed)
      window.removeEventListener('message', handleMessage)
      try {
        if (popup && !popup.closed) {
          popup.close()
        }
      } catch {
        // Ignore errors when closing popup
      }
    }

    // Add message listener
    window.addEventListener('message', handleMessage)

    // Fallback timeout (5 minutes)
    setTimeout(() => {
      if (!resolved) {
        resolved = true
        cleanup()
        resolve({
          success: false,
          error: 'OAuth flow timed out'
        })
      }
    }, 5 * 60 * 1000)
  })
}

/**
 * Send OAuth result from popup to opener window
 * Call this from the OAuth callback page when running in a popup
 */
export function sendOAuthResultToOpener(result: OAuthPopupResult) {
  if (window.opener) {
    window.opener.postMessage(
      {
        type: 'oauth-callback',
        ...result
      },
      window.location.origin
    )
  }
}

/**
 * Check if current window is a popup (has an opener)
 */
export function isPopupWindow(): boolean {
  return !!window.opener && window.opener !== window
}

